import { COLOR_FAMILIES, MAX_COLOR_OPTION_NAME_LENGTH } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import { Button, FileDropField, Input } from '@/shared/ui';
import { COLOR_FAMILY_LABELS, type ColorFormValues } from '../model/color-form';

interface ColorRowProps {
  readonly index: number;
  readonly register: UseFormRegister<ColorFormValues>;
  /** 수급 상태 라벨 표시용 실시간 값. */
  readonly isAvailable: boolean;
  readonly previewKey: string | null;
  readonly previewUrl: string | null;
  readonly isUploading: boolean;
  readonly onPreviewUpload: (index: number, file: File) => void;
  readonly onPreviewClear: (index: number) => void;
  readonly onRemove: (index: number) => void;
}

/** 색상 옵션 표의 한 행 — 스와치·이름·색상군·수급·사진·삭제. */
export const ColorRow = ({
  index,
  register,
  isAvailable,
  previewKey,
  previewUrl,
  isUploading,
  onPreviewUpload,
  onPreviewClear,
  onRemove,
}: ColorRowProps): ReactElement => (
  <tr className="border-t align-middle">
    <td className="px-3 py-2">
      <input
        type="color"
        aria-label={`색상 ${index + 1} 코드`}
        className="size-8 cursor-pointer rounded border"
        {...register(`colorOptions.${index}.colorCode`)}
      />
    </td>
    <td className="px-3 py-2">
      <Input
        aria-label={`색상 ${index + 1} 이름`}
        className="h-8 w-32"
        placeholder="매트 블랙"
        maxLength={MAX_COLOR_OPTION_NAME_LENGTH}
        {...register(`colorOptions.${index}.name`, { required: true })}
      />
    </td>
    <td className="px-3 py-2">
      <select
        aria-label={`색상 ${index + 1} 색상군`}
        className="h-8 rounded-md border bg-background px-2 text-sm"
        {...register(`colorOptions.${index}.colorFamily`)}
      >
        {COLOR_FAMILIES.map((family) => (
          <option key={family} value={family}>
            {COLOR_FAMILY_LABELS[family]}
          </option>
        ))}
      </select>
    </td>
    <td className="px-3 py-2">
      <label className="flex items-center gap-1.5 whitespace-nowrap">
        <input type="checkbox" {...register(`colorOptions.${index}.isAvailable`)} />
        <span className={isAvailable ? undefined : 'font-medium text-destructive'}>
          {isAvailable ? '선택 가능' : '수급 불가'}
        </span>
      </label>
    </td>
    <td className="px-3 py-2">
      {previewKey ? (
        <span className="flex items-center gap-1.5">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`색상 ${index + 1} 미리보기`}
              className="size-8 rounded border object-cover"
            />
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={() => onPreviewClear(index)}>
            제거
          </Button>
        </span>
      ) : (
        <FileDropField
          compact
          accept="image/jpeg,image/png,image/webp"
          aria-label={`색상 ${index + 1} 미리보기 업로드`}
          buttonLabel="미리보기 올리기"
          disabled={isUploading}
          onFiles={([file]) => {
            if (file) {
              onPreviewUpload(index, file);
            }
          }}
        />
      )}
    </td>
    <td className="px-3 py-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive"
        aria-label={`색상 ${index + 1} 삭제`}
        onClick={() => onRemove(index)}
      >
        삭제
      </Button>
    </td>
  </tr>
);
